import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import API from '../../api/API';
import { useMatchForm } from './hooks/useMatchForm';
import { useMatchAPI } from './hooks/useMatchAPI';
import { useMatchData } from './hooks/useMatchData';
import { isMatchConfigComplete } from './utils/matchUtils';
import LoadingSpinner from './components/LoadingSpinner';
import NoEditionMessage from './components/NoEditionMessage';
import MatchConfigurationPanel from './MatchConfigurationPanel';
import PlayersManager from './PlayersManager';

const MatchEditor = ({ isSaving, setIsSaving }) => {
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const matchForm = useMatchForm();
  const matchAPI = useMatchAPI();
  const { loading, caricaPartita } = useMatchData(searchParams, id, matchForm, matchAPI);
  const [torneoStato, setTorneoStato] = useState(null);

  // le partite si gestiscono solo a torneo in corso (avviato e non chiuso)
  useEffect(() => {
    const idEdizione = matchForm.edizioneSelezionata?.id;
    if (!idEdizione) return;
    API.torneo.dettaglio(idEdizione).then((r) => {
      if (r.isSuccess && r.data) setTorneoStato(r.data.stato);
    });
  }, [matchForm.edizioneSelezionata?.id]);

  const partiteBloccate = torneoStato != null && torneoStato !== 'inCorso';
  const motivoBlocco = torneoStato === 'preparazione'
    ? "Il torneo non è ancora stato avviato: avvialo dalla pagina Planning per inserire le partite."
    : 'Il torneo è chiuso: le partite non sono più modificabili.';

  // Le distinte non stanno piu' sulle squadre dell'elenco, che portano solo id e nome: appena si
  // conosce la coppia le si chiede al server, insieme al punteggio se la partita esiste gia'.
  const handleSquadra1Change = async (e) => {
    const squadraId = parseInt(e.target.value);
    const squadra = matchForm.squadreDisponibili.find(sq => sq.id === squadraId);
    matchForm.setSquadra1(squadra);

    if (matchForm.squadra2 && matchForm.squadra2.id === squadraId) {
      matchForm.setSquadra2(null);
      return;
    }

    if (squadra && matchForm.squadra2) {
      await caricaPartita(squadra.id, matchForm.squadra2.id);
    }
  };

  const handleSquadra2Change = async (e) => {
    const squadraId = parseInt(e.target.value);
    const squadra = matchForm.squadreDisponibili.find(sq => sq.id === squadraId);
    matchForm.setSquadra2(squadra);

    if (squadra && matchForm.squadra1) {
      await caricaPartita(matchForm.squadra1.id, squadra.id);
    }
  };

  const handleSalvaPartita = async () => {
    setIsSaving(true);
    const result = await matchAPI.salvaPartita({
      isEditMode: matchForm.isEditMode,
      partitaCorrente: matchForm.partitaCorrente,
      edizioneSelezionata: matchForm.edizioneSelezionata,
      squadra1: matchForm.squadra1,
      squadra2: matchForm.squadra2,
      giocatoriSquadra1: matchForm.giocatoriSquadra1,
      giocatoriSquadra2: matchForm.giocatoriSquadra2,
      falli1: matchForm.falli1,
      falli2: matchForm.falli2,
      fairPlay1: matchForm.fairPlay1,
      fairPlay2: matchForm.fairPlay2,
      tipoPartita: matchForm.tipoPartita,
      fasciaPartita: matchForm.fasciaPartita,
      nome: searchParams.get('nome')
    });

    if (result.success) {
      enqueueSnackbar(result.message, { variant: 'success' });

      // Una sola ricarica dal server: conferma quello che e' stato scritto e, se la partita era
      // nuova, porta in casa il suo id. Prima l'id si copiava a mano dalla risposta del salvataggio
      // e poi si ricaricava comunque.
      if (matchForm.squadra1 && matchForm.squadra2) {
        await caricaPartita(
          matchForm.squadra1.id,
          matchForm.squadra2.id,
          matchForm.tipoPartita || null,
          searchParams.get('nome')
        );
      }
    } else {
      enqueueSnackbar(result.message, { variant: 'error' });
    }
    setIsSaving(false);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!matchForm.edizioneSelezionata) {
    return <NoEditionMessage />;
  }

  const isConfigComplete = isMatchConfigComplete(
    matchForm.squadra1, 
    matchForm.squadra2, 
    matchForm.tipoPartita, 
    matchForm.fasciaPartita
  );

  return (
    <div className="match-partite-container">
      {partiteBloccate && (
        <div className="match-stato-banner" title={motivoBlocco}>
          <i className="fas fa-lock"></i>
          {motivoBlocco}
        </div>
      )}

      {/* Configurazione Panel - sempre visibile */}
      <MatchConfigurationPanel
        saveDisabled={partiteBloccate}
        saveDisabledReason={motivoBlocco}
        squadreDisponibili={matchForm.squadreDisponibili}
        squadra1={matchForm.squadra1}
        squadra2={matchForm.squadra2}
        tipoPartita={matchForm.tipoPartita}
        fasciaPartita={matchForm.fasciaPartita}
        isReadonly={matchForm.isReadonly}
        isEditMode={matchForm.isEditMode}
        partitaCorrente={matchForm.partitaCorrente}
        isConfigComplete={isConfigComplete}
        onSquadra1Change={handleSquadra1Change}
        onSquadra2Change={handleSquadra2Change}
        onTipoChange={matchForm.setTipoPartita}
        onFasciaChange={matchForm.setFasciaPartita}
        onSave={handleSalvaPartita}
        isSaving={isSaving}
        onReset={matchForm.resetForm}
      />

      {/* Gestione Giocatori - visibile solo quando la configurazione è completa */}
      {isConfigComplete ? (
        <PlayersManager
          squadra1={matchForm.squadra1}
          squadra2={matchForm.squadra2}
          giocatoriSquadra1={matchForm.giocatoriSquadra1}
          giocatoriSquadra2={matchForm.giocatoriSquadra2}
          falli1={matchForm.falli1}
          falli2={matchForm.falli2}
          fairPlay1={matchForm.fairPlay1}
          fairPlay2={matchForm.fairPlay2}
          tipoPartita={matchForm.tipoPartita}
          onUpdateGiocatore={matchForm.updateGiocatore}
          onToggleCartellino={matchForm.toggleCartellino}
          onUpdateFalli1={matchForm.setFalli1}
          onUpdateFalli2={matchForm.setFalli2}
          onUpdateFairPlay1={matchForm.setFairPlay1}
          onUpdateFairPlay2={matchForm.setFairPlay2}
        />
      ) : (
        <div className="match-partite-setup">
          <div className="match-setup-card">
            <div className="match-setup-actions">
              <p className="match-help-text">Completa tutti i campi di configurazione per iniziare l'inserimento dei risultati</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchEditor;